import Typography from "@mui/material/Typography";
import {
    getLodgingCoverage,
    getMealCoverage,
    getActivityCoverage
} from './EventFeatureAnalyzer';


function scoreEvent(event, currentUserId) {

    const isHostOrPlanner =
        event.host.includes(currentUserId) ||
        !!event.attendees.find(_att =>
            _att.id === currentUserId && _att.role === 'planner'
        );


    if (!isHostOrPlanner) {
        return {
            eventId: event.id,
            score: 0,
            qualifiesForTier: false
        };
    }

    let score = 0;

    if (event.attendees && event.attendees.length > 1) score += 1; // more than just the host
    if (event.lodging?.length || event.meals?.length || event.activities?.length) score += 1;
    if (event.rsvpSent || event.to_do?.length || event.messages?.length) score += 1;
    if (event.budget || event.receipts?.length) score += 1;
    if (event.photos?.length || event.supplies?.length || event.ideas?.length) score += 1;
    if (event.aiUsed) score += 1;

    return {
        eventId: event.id,
        score,
        qualifiesForTier: score >= 2
    };
}

function getUserTier(qualifiedEventCount) {
    if (qualifiedEventCount >= 51) return 'Legendary';
    if (qualifiedEventCount >= 21) return 'Master';
    if (qualifiedEventCount >= 11) return 'Pro';
    if (qualifiedEventCount >= 4) return 'Rising Star';
    return 'Newbie';
}

export function summarizeFeatureUse(events) {
    const featureTotals = {
        lodging: 0,
        meals: 0,
        activities: 0,
        photos: 0,
        ideas: 0,
        supplies: 0,
        receipts: 0,
        budget: 0,
        messaging: 0,
        aiUsed: 0
    };

    const coverageSummary = {
        lodging: [],
        meals: {
            breakfast: [],
            brunch: [],
            lunch: [],
            dinner: []
        },
        activities: []
    };

    events.forEach(event => {
        if (event.lodging?.length) {
            featureTotals.lodging += 1;
            const lodgingCoverage = getLodgingCoverage(event);
            coverageSummary.lodging.push(lodgingCoverage);
        }

        if (event.meals?.length) {
            featureTotals.meals += 1;
            const mealCoverage = getMealCoverage(event);
            Object.keys(coverageSummary.meals).forEach(type => {
                coverageSummary.meals[type].push(mealCoverage[type] || 0);
            });
        }

        if (event.activities?.length) {
            featureTotals.activities += 1;
            const activityCoverage = getActivityCoverage(event);
            coverageSummary.activities.push(activityCoverage);
        }

        if (event.photos?.length) featureTotals.photos += 1;
        if (event.ideas?.length) featureTotals.ideas += 1;
        if (event.supplies?.length) featureTotals.supplies += 1;
        if (event.receipts?.length) featureTotals.receipts += 1;
        if (typeof event.budget !== 'undefined') featureTotals.budget += 1;
        if (event.messages?.length) featureTotals.messaging += 1;
        if (event.aiUsed) featureTotals.aiUsed += 1;
    });

    const sorted = Object.entries(featureTotals).sort((a, b) => b[1] - a[1]);

    const average = arr => arr.length ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length) : 0;

    return {
        top: sorted.slice(0, 3).map(([key]) => key),
        low: sorted.slice(-3).map(([key]) => key),
        coverage: {
            lodging: average(coverageSummary.lodging),
            meals: {
                breakfast: average(coverageSummary.meals.breakfast),
                brunch: average(coverageSummary.meals.brunch),
                lunch: average(coverageSummary.meals.lunch),
                dinner: average(coverageSummary.meals.dinner)
            },
            activities: average(coverageSummary.activities)
        }
    };
}


export function generateNudge(tier, usage, coverage) {
    const { low, top } = usage;
    const mealCoverage = coverage?.meals || {};
    const nudges = [];

    // ✅ Top Feature Reinforcement
    if (top.includes('lodging')) {
        nudges.push("Nice job planning accommodations — lodging is well covered and that sets the tone for a smooth trip.");
    }

    if (top.includes('meals')) {
        nudges.push("You're doing great organizing meals — food coordination keeps everyone happy and on schedule.");
    }

    if (top.includes('activities')) {
        nudges.push("Activities are well planned — you're clearly focused on creating a memorable experience.");
    }

    if (top.includes('photos')) {
        nudges.push("Love all the photos you’ve been adding — keep capturing those memories!");
    }

    if (top.includes('ideas')) {
        nudges.push("Great use of Ideas — they're a flexible way to explore before committing.");
    }

    if (top.includes('supplies')) {
        nudges.push("Supplies are looking solid — keeping the group prepared really makes a difference.");
    }

    if (top.includes('receipts')) {
        nudges.push("Receipts are well-managed — budgeting and business tracking is on point.");
    }

    // 🛏️ Lodging Coverage Check
    if (coverage.lodging < 50) {
        nudges.push("Some days are missing lodging — want to double-check where everyone’s staying?");
    } else if (coverage.lodging >= 90) {
        nudges.push("You've covered nearly every day with lodging — excellent attention to detail!");
    }

    // 🍽️ Meal Coverage Check
    const lowMealTypes = Object.entries(mealCoverage)
        .filter(([_, percent]) => percent < 40)
        .map(([meal]) => meal);

    if (lowMealTypes.length > 0) {
        const mealStr = lowMealTypes.join(', ');
        nudges.push(`Looks like there aren’t many plans for ${mealStr} — adding a few can help structure the days.`);
    } else if (Object.values(mealCoverage).some(val => val >= 80)) {
        nudges.push("You're keeping the crew well-fed — love the consistency across meal planning.");
    }

    // 🎯 Activity Coverage
    if (coverage.activities < 40) {
        nudges.push("There’s room for more activities — consider adding something fun to fill the gaps.");
    } else if (coverage.activities >= 80) {
        nudges.push("You're making this more than just a trip — great job packing in experiences.");
    }

    // 🔍 Low Feature Usage Nudges
    if (low.includes('photos')) {
        nudges.push("Try adding photos to meals, lodging, or activities — it’s a great way to build the group memory book.");
    }

    if (low.includes('ideas')) {
        nudges.push("Ideas are a handy way to brainstorm and share — give them a try before finalizing anything.");
    }

    if (low.includes('supplies')) {
        nudges.push("Try using Supplies to assign or prepare packing lists — it helps avoid last-minute scrambles.");
    }

    if (low.includes('receipts')) {
        nudges.push("Snap a photo of receipts next time — it helps for personal and business budgeting later.");
    }

    if (low.includes('budget')) {
        nudges.push("Consider setting a budget — even a rough one helps attendees plan better.");
    }

    if (low.includes('messaging')) {
        nudges.push("Messaging can keep your group aligned and excited — try sending a quick update or poll.");
    }

    if (low.includes('aiUsed')) {
        nudges.push("Don't forget — your AI Concierge can help plan activities, meals, and more.");
    }

    // ✨ Fallback
    if (nudges.length === 0) {
        return "You're doing great — keep exploring new features to level up your event planning skills!";
    }

    return nudges;
}




export function TierProgress({ currentTier, qualifiedEventCount }) {
    const tiers = {
        Newbie: { next: 'Rising Star', required: 4 },
        'Rising Star': { next: 'Pro', required: 11 },
        Pro: { next: 'Master', required: 21 },
        Master: { next: 'Legendary', required: 51 },
        Legendary: { next: null, required: null }
    };

    const tierInfo = tiers[currentTier];
    const progress = tierInfo.next
        ? (qualifiedEventCount / tierInfo.required) * 100
        : 100;

    return tierInfo.next ? (
        <div style={{ marginTop: 12 }}>
            <p>Next tier: <strong>{tierInfo.next}</strong> ({qualifiedEventCount}/{tierInfo.required})</p>
            <div style={{
                height: 8,
                borderRadius: 4,
                background: '#eee',
                overflow: 'hidden'
            }}>
                <div style={{
                    width: `${Math.min(progress, 100)}%`,
                    background: '#f09637',
                    height: '100%'
                }} />
            </div>
        </div>
    ) : (
        <p>You’ve reached the top tier — Legendary status achieved! 🏆</p>
    );
}

export function generateSuggestions({ tier, recentFeaturesUsed, lowUsageFeatures, coverage }) {
    const suggestions = [];

    // ✅ Lodging: check coverage %
    if (recentFeaturesUsed.includes('lodging')) {
        if (coverage?.lodging >= 80) {
            suggestions.push("You're clearly thoughtful about where everyone stays — great job managing accommodations!");
        } else {
            suggestions.push("Lodging is started — a few more days covered can help guests feel more at ease.");
        }
    }

    // ✅ Meals: check average across all meal types
    if (recentFeaturesUsed.includes('meals')) {
        const mealCoverage = coverage?.meals || {};
        const avgMealCoverage =
            Object.values(mealCoverage).length > 0
                ? Object.values(mealCoverage).reduce((sum, val) => sum + val, 0) / Object.values(mealCoverage).length
                : 0;

        if (avgMealCoverage >= 60) {
            suggestions.push("Your meal planning is on point — food brings people together and you’re making it count.");
        } else {
            suggestions.push("You're starting to build out meals — a few more added throughout the week could round things out nicely.");
        }
    }

    // ✅ Activities: check coverage %
    if (recentFeaturesUsed.includes('activities')) {
        if (coverage?.activities >= 60) {
            suggestions.push("You're creating experiences, not just events — love the attention to activities!");
        } else {
            suggestions.push("Activities are a great way to elevate your event — consider adding something fun each day.");
        }
    }

    // ✅ Photos: reinforce if used a few times
    if (recentFeaturesUsed.includes('photos')) {
        suggestions.push("You're capturing memories like a pro — keep those event moments coming!");
    }

    // ✅ Supplies: basic presence check
    if (recentFeaturesUsed.includes('supplies')) {
        suggestions.push("Your group must love you — you're keeping everyone prepared and organized.");
    }

    // ✅ Receipts: basic presence check
    if (recentFeaturesUsed.includes('receipts')) {
        suggestions.push("You're tracking receipts like a pro — budget control is a game changer.");
    }

    // 💡 Underused features (nudges)
    if (lowUsageFeatures.includes('ideas')) {
        suggestions.push("Use Ideas to brainstorm without committing — a great way to test thoughts before locking them in.");
    }

    if (lowUsageFeatures.includes('budget')) {
        suggestions.push("Try setting a budget — it helps everyone stay aligned on spending, especially for shared costs.");
    }

    if (lowUsageFeatures.includes('messaging')) {
        suggestions.push("Start a conversation — group messaging helps align plans, share updates, and build hype.");
    }

    if (lowUsageFeatures.includes('photos') && !recentFeaturesUsed.includes('photos')) {
        suggestions.push("Try uploading photos to meals, lodging, or activities — attendees can add their own too!");
    }

    if (lowUsageFeatures.includes('supplies') && !recentFeaturesUsed.includes('supplies')) {
        suggestions.push("Shared supply lists can make packing easier — and help avoid that one person who forgets everything 😅.");
    }

    if (lowUsageFeatures.includes('receipts') && !recentFeaturesUsed.includes('receipts')) {
        suggestions.push("Receipts can help track costs and business reimbursements — just snap and save.");
    }

    // 🧠 Tier-based encouragement
    if (tier === 'Newbie') {
        suggestions.push("Each action you take — like adding meals or inviting attendees — brings you closer to your next tier.");
    }

    if (tier === 'Rising Star') {
        suggestions.push("You've hosted several events already — consider saving your layout as a Template for next time.");
    }

    if (tier === 'Pro') {
        suggestions.push("With 10+ events under your belt, you’re ready to automate — try streamlining things with shared supply kits or reusable groups.");
    }

    return suggestions;
}




function SuggestionsBlock({ suggestions }) {
    if (!suggestions?.length) return null;

    return (
        <div style={{ marginTop: 24 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                💡 Suggestions for This Event
            </Typography>
            <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
                {suggestions.map((tip, index) => (
                    <li key={index} style={{ marginBottom: 8 }}>
                        <Typography variant="body2" sx={{ color: '#555' }}>
                            {tip}
                        </Typography>
                    </li>
                ))}
            </ul>
        </div>
    );
}
export { SuggestionsBlock };

export function runEventAnalyzer({ currentUser, allEvents, currentEventId }) {
    const scoredEvents = allEvents.map(e => scoreEvent(e, currentUser.id));
    const qualifiedEvents = scoredEvents.filter(e => e.qualifiesForTier);

    const tier = getUserTier(qualifiedEvents.length);

    const currentEvent = currentEventId
        ? scoredEvents.find(e => e.eventId === currentEventId)
        : null;

    const featureUsage = summarizeFeatureUse(allEvents);
    const { top, low, coverage } = featureUsage;
    const aiEngagement = currentUser.ai?.totalInteractions || 0;

    const engagementLevel =
        aiEngagement === 0 ? "none" :
            aiEngagement < 5 ? "low" :
                aiEngagement < 20 ? "medium" : "high";

    return {
        tier,
        eventCount: allEvents.length,
        qualifiedEventCount: qualifiedEvents.length,
        currentEventIsNew: currentEvent ? currentEvent.score < 2 : true,
        currentEventId,
        recentFeaturesUsed: top,
        lowUsageFeatures: low,
        coverage, // ✅ <-- add this
        aiConcierge: {
            name: currentUser.ai?.name || "Your Concierge",
            engagementLevel,
            audibleEnabled: currentUser.ai?.audibleEnabled || false
        },
        nudge: null, // Placeholder
        dialogContentBlockId: `${tier.toLowerCase()}_event_${qualifiedEvents.length + 1}`
    };
}
